import { useState } from 'react';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import { useMsal } from '@azure/msal-react'
import Storage from 'src/utils/storage/storage';
import { analytics_service } from '../services/analytics';
import { MatomoEventTrackMapObject } from '../pages/analytics/simulator/constants/constant';
import fetchUserMetadata from '../components/user/user/api/user-api';
import { getSurveyImplementation, getMatomoSurveyToolName } from '../constants';
import matomoConfig from '../utils/matomoConfig';

type FunctionName =
  | 'track_page_view'
  | 'track_user_details'
  | 'track_button_click'
  | 'track_simulate_button_click'
  | 'track_download_button_click'
  | 'track_create_scenario_button_click'
  | 'track_tab_switch'
  | 'track_load_saved_scenario_button_click'
  | 'track_survey_visit'
  | '';

function useMatomoFunction() {
  const [value, setValue] = useState<string>('');
  const { pushInstruction, trackEvent } = useMatomo();
  const { accounts } = useMsal(); // Get user accounts from Azure MSAL for authentication

  // function1 - trackPageView (we will add more functions later)
  const trackPageView = (page_title: string) => {
    analytics_service.trackPageView(page_title);
  };
  const segmentDetailsStorage = Storage.getSegmentDetails();
  const country = segmentDetailsStorage?.source_name || '';
  const surveyImplementation = getSurveyImplementation(country);
  const toolName = getMatomoSurveyToolName(country);

  const trackUserDetails = async () => {
    const { userMeta } = Storage.getUserMetadata();
    if (userMeta?.user_email) {
      pushInstruction('setUserId', userMeta.user_email);
    } else {
      try {
        const response = await fetchUserMetadata();
        if (response?.data?.length > 0) {
          Storage.setUserMetadata({
            userMeta: response.data[0],
            screenMeta: []
          });
          // get userMeta 
          const { userMeta } = Storage.getUserMetadata();
          pushInstruction('setUserId', userMeta?.user_email);
          if (userMeta?.user_email) {
            pushInstruction('setUserId', userMeta.user_email);
          } else {
            console.warn('Matomo: user_email still missing after refresh');
          }
        }
      } catch (error) {
        console.error('Error refreshing token and fetching user metadata', error);
      }
    }
  };

  const getEnvironmentUrls = () => {
    const windowsUrl = window.location.href;
    const isProd = windowsUrl.includes('mars-ppaanalyticedge-fe.cloud-effem.com'); // Check if running in production

    return {
      finalHREF: isProd
        ? surveyImplementation.routePathProd
        : surveyImplementation.routePathDev,
      finalAPIURL: isProd
        ? surveyImplementation.apiUrlProd
        : surveyImplementation.apiUrlDev,
    };
  };

  /**
   * handleSurveyAPI()
   * 
   * Purpose: Makes an API call to log user survey visit/participation
   * This function tracks when users visit the application for analytics purposes
   * 
   * How it works:
   * 1. Gets the environment-specific API URL using getEnvironmentUrls()
   * 2. Checks if user accounts are available (authenticated user from MSAL)
   * 3. Makes a POST request to the survey API with the following data:
   *    - tool: Application name/identifier (e.g., 'TOOL-NAME')
   *    - user: Current user's email/username from Azure authentication
   * 4. If the API response is not OK, throws an error
   * 5. Catches and logs any errors that occur during the API call
   */

  const handleSurveyAPI = async () => {
    const { finalAPIURL, finalHREF } = getEnvironmentUrls();

    try {
      // Check if user is authenticated with at least one account
      if (accounts && accounts.length > 0) {
        // Make POST request to track user survey visit
        const response = await fetch(finalAPIURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tool: toolName,
            user: accounts[0].username,
          }),
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        // console.log('Survey API call successful');

        // // Open survey link in new window after successful API call
        // window.open(finalHREF, '_blank');
      } else {
        console.warn('No user accounts found');
      }
    } catch (error) {
      console.error('Error occurred:', error);
    }
  };

  const trackClickEvent = (page_title: string, action_name: string = null) => {
    trackEvent({ category: page_title, action: action_name ?? 'click-event' });
  };

  const callFunction = async (funcName: FunctionName | string, ...args: any[]) => {
    // Disable tracking and its associated survey/user-metadata requests together.
    if (!matomoConfig.ANALYTICS.active) {
      return;
    }

    await handleSurveyAPI();
    // page tracking function
    if (funcName === 'track_page_view') {
      trackPageView(...(args as [any]));
    }
    // user details tracking function
    else if (funcName === 'track_user_details') {
      trackUserDetails();
    }
    // event tracking function
    else if (MatomoEventTrackMapObject[funcName]) {
      trackClickEvent(
        ...(args as [any]),
        MatomoEventTrackMapObject[funcName].event_label
      );
    } else {
      setValue('Invalid function name');
    }
  };

  return {
    callFunction,
    value
  };
}

export default useMatomoFunction;
